import React, { useMemo, useState } from "react";
import {
    CalendarDays,
    Download,
    RefreshCcw,
    Search,
    Edit3,
    X,
    AlertTriangle,
    IndianRupee,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import useSettlement from "../Services/OrderServices/SettlementServices/useSettlement";

const PAGE_SIZE = 200;

export default function AdminReviseMapReport() {
    const {
        getRestaurantWiseTransactions,
        updateSettlementTransaction,
        updateOrderAmount,
    } = useSettlement();

    const today = () => new Date().toISOString().slice(0, 10);

    const [date, setDate] = useState(today());
    const [rows, setRows] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        userid: "",
        orderid: "",
        subtotal: "",
        orderAmount: "",
        payTotal: "",
        taxsgst: "",
        taxcgst: "",
        taxOnCommision: "",
        chaafoCommision: "",
        settlementAmount: "",
    });

    const fetchReviseMap = async () => {
        if (!date) {
            toast.error("Please select date");
            return;
        }

        setLoading(true);
        try {
            const result = await getRestaurantWiseTransactions({
                userid: "0",
                date,
            });

            if (result.success) {
                setRows(Array.isArray(result.data) ? result.data : []);
                setPage(1);
                setSearched(true);
                toast.success(result.message || "Data fetched successfully");
            } else {
                setRows([]);
                setSearched(true);
                toast.error(result.message || "Failed to fetch data");
            }
        } catch (error) {
            console.error("fetchReviseMap error:", error);
            setRows([]);
            setSearched(true);
            toast.error("Failed to fetch revise map data");
        } finally {
            setLoading(false);
        }
    };

    const openUpdateModal = (row) => {
        setSelectedRow(row);
        setConfirmOpen(true);
    };

    const closeUpdateModal = () => {
        if (updateLoading) return;
        setConfirmOpen(false);
        setSelectedRow(null);
    };

    const handleUpdateSettlement = async () => {
        if (!selectedRow) return;

        setUpdateLoading(true);
        try {
            const result = await updateSettlementTransaction({
                userid: selectedRow?.fk_user_id ?? "",
                orderid: selectedRow?.order_id ?? "",
                pk_orderid: selectedRow?.pk_order_id ?? "",
                ordertype: selectedRow?.order_type ?? "",
            });

            if (result.success) {
                toast.success(result.message || "Updated successfully");
                closeUpdateModal();
                await fetchReviseMap();
            } else {
                toast.error(result.message || "Update failed");
            }
        } catch (e) {
            console.error("handleUpdateSettlement error:", e);
            toast.error("Failed to update settlement transaction");
        } finally {
            setUpdateLoading(false);
        }
    };
    const normalizeAmountValue = (value, decimals = 2) => {
        if (value === null || value === undefined) return "";

        const cleaned = String(value).replace(/[^0-9.-]/g, "").trim();

        if (!cleaned || cleaned === "." || cleaned === "-" || cleaned === "-.") {
            return "";
        }

        const num = Number(cleaned);

        if (Number.isNaN(num)) return "";

        return num.toFixed(decimals);
    };

    const sanitizeAmountInput = (value, decimals = 2) => {
        let cleaned = String(value ?? "").replace(/[^0-9.]/g, "");

        // allow only one dot
        const firstDotIndex = cleaned.indexOf(".");
        if (firstDotIndex !== -1) {
            cleaned =
                cleaned.slice(0, firstDotIndex + 1) +
                cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
        }

        const [whole = "", decimal = ""] = cleaned.split(".");
        const limitedDecimal = decimal.slice(0, decimals);

        return cleaned.includes(".")
            ? `${whole}.${limitedDecimal}`
            : whole;
    };

    const formatPaymentFormValues = (data = {}) => ({
        userid: data?.userid ?? "",
        orderid: data?.orderid ?? "",
        subtotal: normalizeAmountValue(data?.subtotal),
        orderAmount: normalizeAmountValue(data?.orderAmount),
        payTotal: normalizeAmountValue(data?.payTotal),
        taxsgst: normalizeAmountValue(data?.taxsgst),
        taxcgst: normalizeAmountValue(data?.taxcgst),
        taxOnCommision: normalizeAmountValue(data?.taxOnCommision),
        chaafoCommision: normalizeAmountValue(data?.chaafoCommision),
        settlementAmount: normalizeAmountValue(data?.settlementAmount),
    });
    const handlePaymentUpdate = async () => {
        if (!paymentForm.userid) {
            toast.error("User ID is missing");
            return;
        }

        if (!paymentForm.orderid) {
            toast.error("Order ID is missing");
            return;
        }

        const normalizedForm = formatPaymentFormValues(paymentForm);

        const fieldsToValidate = [
            { key: "subtotal", label: "Subtotal" },
            { key: "orderAmount", label: "Order Amount" },
            { key: "payTotal", label: "Pay Total" },
            { key: "taxsgst", label: "Tax SGST" },
            { key: "taxcgst", label: "Tax CGST" },
            { key: "taxOnCommision", label: "Tax On Commission" },
            { key: "chaafoCommision", label: "Chaafo Commission" },
            { key: "settlementAmount", label: "Settlement Amount" },
        ];

        for (const field of fieldsToValidate) {
            const value = normalizedForm[field.key];

            if (value === "") {
                toast.error(`${field.label} is required`);
                return;
            }

            if (!isValidAmount(value)) {
                toast.error(`Enter valid ${field.label}`);
                return;
            }
        }

        setPaymentLoading(true);
        try {
            const result = await updateOrderAmount(normalizedForm);

            if (result.success) {
                toast.success(result.message || "Payment updated successfully");
                closePaymentModal();
                await fetchReviseMap();
            } else {
                toast.error(result.message || "Payment update failed");
            }
        } catch (e) {
            console.error("handlePaymentUpdate error:", e);
            toast.error("Failed to update payment details");
        } finally {
            setPaymentLoading(false);
        }
    };
    const exportExcel = () => {
        if (!rows.length) {
            toast.error("No data to export");
            return;
        }

        try {
            const exportRows = rows.map((r, i) => ({
                sn: i + 1,
                pk_order_id: r?.pk_order_id || "",
                us_name: r?.us_name,
                order_date: r?.order_date,
                fk_user_id: r?.fk_user_id || "",
                order_id: r?.order_id || "",
                daily_order_number: r?.daily_order_number || "",
                order_type: r?.order_type || "",
                subtotal: r?.subtotal || "0",
                order_amount: r?.order_amount || "0",
                pay_amount: r?.pay_amount || "0",
                customer_mobile: r?.customer_mobile || "",
                customer_name: r?.customer_name || "",
                status: r?.status || "",
                payment_status: r?.payment_status || "",
                tax_sgst: r?.tax_sgst || "0",
                tax_cgst: r?.tax_cgst || "0",
                payment_link: r?.payment_link || "",
                taxOnCommission: r?.taxOnCommission || "0",
                chaafoCommission: r?.chaafoCommission || "0",
                settelment_value: r?.settelment_value || "0",
                delivery_fee: r?.delivery_fee || "0",
            }));

            const ws = XLSX.utils.json_to_sheet(exportRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ReviseMap");

            const excelBuffer = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
            });

            saveAs(
                new Blob([excelBuffer], { type: "application/octet-stream" }),
                `ReviseMap_${date}.xlsx`,
            );

            toast.success("Excel exported");
        } catch (e) {
            console.error("exportExcel error:", e);
            toast.error("Export failed");
        }
    };

    const money = (v) =>
        v == null || isNaN(Number(v)) ? "₹ 0.00" : `₹ ${Number(v).toFixed(2)}`;

    const filteredRows = useMemo(() => {
        if (!search) return rows;

        const s = search.toLowerCase();

        return rows.filter((r) =>
            (r?.order_id || "").toLowerCase().includes(s) ||
            (r?.customer_name || "").toLowerCase().includes(s) ||
            (r?.customer_mobile || "").toLowerCase().includes(s) ||
            (r?.pk_order_id || "").toLowerCase().includes(s) ||
            (r?.fk_user_id || "").toLowerCase().includes(s)
        );
    }, [rows, search]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

    const paginatedRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredRows.slice(start, start + PAGE_SIZE);
    }, [filteredRows, page]);

    const isValidAmount = (value) => {
        if (value === "" || value === null || value === undefined) return false;
        return /^\d+(\.\d{1,2})?$/.test(String(value).trim());
    };

    const openPaymentModal = (row) => {
        setSelectedRow(row);

        setPaymentForm(
            formatPaymentFormValues({
                userid: row?.fk_user_id ?? "",
                orderid: row?.order_id ?? "",
                subtotal: row?.subtotal,
                orderAmount: row?.order_amount,
                payTotal: row?.pay_amount,
                taxsgst: row?.tax_sgst,
                taxcgst: row?.tax_cgst,
                taxOnCommision: row?.taxOnCommission,
                chaafoCommision: row?.chaafoCommission,
                settlementAmount: row?.settelment_value,
            })
        );

        setPaymentModalOpen(true);
    };

    const closePaymentModal = () => {
        if (paymentLoading) return;
        setPaymentModalOpen(false);
        setSelectedRow(null);
        setPaymentForm({
            userid: "",
            orderid: "",
            subtotal: "",
            orderAmount: "",
            payTotal: "",
            taxsgst: "",
            taxcgst: "",
            taxOnCommision: "",
            chaafoCommision: "",
            settlementAmount: "",
        });
    };

    const handlePaymentInput = (field, value) => {
        const finalValue = sanitizeAmountInput(value, 2);

        setPaymentForm((prev) => ({
            ...prev,
            [field]: finalValue,
        }));
    };

    return (
        <div>
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Revise Map</h2>
                    <p className="text-sm text-gray-500">
                        Fetch restaurant-wise transactions and update settlement mapping
                    </p>
                </div>

                <button
                    className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
                    onClick={fetchReviseMap}
                    disabled={loading}
                >
                    <RefreshCcw size={16} />
                    Refresh
                </button>
            </div>

            <div className="rounded-2xl border bg-white p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <CalendarDays size={16} />
                    Filters
                </div>

                <div className="flex flex-wrap items-end gap-2">
                    <label className="text-sm">
                        <div className="mb-1 text-xs text-gray-500">Search</div>
                        <input
                            type="text"
                            placeholder="Order ID, Customer, Mobile..."
                            className="h-10 w-64 rounded-xl border px-3 text-sm outline-none"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </label>
                    <label className="text-sm">
                        <div className="mb-1 text-xs text-gray-500">Date</div>
                        <input
                            type="date"
                            className="h-10 w-52 rounded-xl border px-3 text-sm outline-none"
                            value={date}
                            max={today()}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </label>

                    <div className="ml-auto flex gap-2">
                        <button
                            className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
                            onClick={exportExcel}
                            disabled={!rows.length || loading}
                        >
                            <Download size={16} />
                            Export Excel
                        </button>

                        <button
                            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#705CFE] hover:bg-[#5B47F5] px-4 text-sm font-semibold text-white  disabled:opacity-60"
                            onClick={fetchReviseMap}
                            disabled={loading}
                        >
                            <Search size={16} />
                            {loading ? "Fetching..." : "Fetch"}
                        </button>
                    </div>
                </div>

                <div className="mt-2 text-xs text-gray-500">
                    Records: <b className="text-gray-700">{filteredRows.length}</b>
                </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
                <div className="overflow-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-[#705CFE] text-white text-center">
                            <tr>
                                {[

                                    "Order ID",
                                    "Restaurant",
                                    "Subtotal",
                                    "Order Amount",
                                    "Pay Amount",
                                    "Customer",
                                    "Payment",
                                    "Tax SGST",
                                    "Tax CGST",
                                    "Tsx Com.",
                                    "Commission",
                                    "Settlement Value",
                                    "Delivery Fee",
                                    "Action",
                                ].map((h) => (
                                    <th key={h} className="whitespace-nowrap px-4 py-2 font-semibold">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {!searched && !loading && (
                                <tr>
                                    <td colSpan={15} className="px-4 py-10 text-center text-gray-500">
                                        Select date and click <b>Fetch</b>.
                                    </td>
                                </tr>
                            )}

                            {searched && !loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={15} className="px-4 py-10 text-center text-gray-500">
                                        No records found for selected date.
                                    </td>
                                </tr>
                            )}

                            {loading && (
                                <tr>
                                    <td colSpan={15} className="px-4 py-10 text-center text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            )}

                            {!loading &&
                                paginatedRows.map((r, idx) => (
                                    <tr key={`${r?.pk_order_id}-${idx}`} className="border-t text-center">

                                        {/* S/N */}


                                        <td className="px-4 py-2 max-w-[160px]">
                                            <div className="flex flex-col leading-tight">

                                                {/* Order ID (clickable) */}
                                                <a
                                                    onClick={() => openPaymentModal(r)}
                                                    className="font-semibold text-blue-700 hover:text-blue-900 underline truncate whitespace-nowrap cursor-pointer"
                                                    title={`#${r?.order_id}`}
                                                >
                                                    #{r?.order_id ?? "—"}
                                                </a>

                                                {/* Order Type */}
                                                <span
                                                    className="text-xs text-gray-500 capitalize truncate whitespace-nowrap"
                                                    title={r?.order_type}
                                                >
                                                    {r?.order_type?.replace("_", " ") ?? "—"}
                                                </span>

                                            </div>
                                        </td>
                                        <td className="px-4 py-2 max-w-[180px]">
                                            <div className="flex flex-col leading-tight">

                                                {/* Name */}
                                                <span
                                                    className="font-semibold text-gray-800 text-sm truncate whitespace-nowrap"
                                                    title={r?.us_name}
                                                >
                                                    {r?.us_name ?? "—"}
                                                </span>

                                                {/* Date */}
                                                <span
                                                    className="text-xs text-gray-500 truncate whitespace-nowrap"
                                                    title={r?.order_date}
                                                >
                                                    {r?.order_date ?? "—"}
                                                </span>

                                            </div>
                                        </td>
                                        {/* Amounts */}
                                        <td className="px-4 py-2">{money(r?.subtotal)}</td>
                                        <td className="px-4 py-2">{money(r?.order_amount)}</td>
                                        <td className="px-4 py-2 font-semibold">
                                            {money(r?.pay_amount)}
                                        </td>

                                        {/* Customer */}
                                        <td className="px-4 py-2">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-800">
                                                    {r?.customer_name ?? "—"}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {r?.customer_mobile ?? "—"}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Payment Status */}
                                        <td className="px-4 py-2">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                                                ✓
                                            </span>
                                        </td>

                                        {/* Taxes */}
                                        <td className="px-4 py-2">{money(r?.tax_sgst)}</td>
                                        <td className="px-4 py-2">{money(r?.tax_cgst)}</td>
                                        <td className="px-4 py-2">{money(r?.taxOnCommission)}</td>

                                        {/* Commission */}
                                        <td className="px-4 py-2">
                                            {money(r?.chaafoCommission)}
                                        </td>

                                        {/* Settlement */}
                                        <td className="px-4 py-2 font-semibold text-[#705CFE]">
                                            {money(r?.settelment_value)}
                                        </td>

                                        {/* Delivery */}
                                        <td className="px-4 py-2">
                                            {money(r?.delivery_fee)}
                                        </td>

                                        {/* Action */}
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => openUpdateModal(r)}
                                                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#01C37D] px-3 text-sm font-medium text-white hover:bg-[#00a96b]"
                                                >
                                                    <Edit3 size={12} />
                                                    Revise
                                                </button>

                                                <button
                                                    onClick={() => openPaymentModal(r)}
                                                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#705CFE] px-3 text-sm font-medium text-white hover:bg-[#5B47F5]"
                                                >
                                                    <IndianRupee size={12} />
                                                    Update
                                                </button>
                                            </div>
                                        </td>

                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                {rows.length > PAGE_SIZE && (
                    <div className="flex items-center justify-center gap-2 border-t p-3">
                        <button
                            className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Prev
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={page === totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
            {/* Revise Confirmation Modal */}
            {confirmOpen && selectedRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-start justify-between border-b p-4">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 rounded-xl bg-amber-100 p-2 text-amber-600">
                                    <AlertTriangle size={18} />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold">Confirm Update</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        This will update settlement transaction for this order.
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={closeUpdateModal}
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                                disabled={updateLoading}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-2 p-4 text-sm">
                            <div><b>PK Order ID:</b> {selectedRow?.pk_order_id ?? "—"}</div>
                            <div><b>User ID:</b> {selectedRow?.fk_user_id ?? "—"}</div>
                            <div><b>Order ID:</b> {selectedRow?.order_id ?? "—"}</div>
                            <div><b>Order Type:</b> {selectedRow?.order_type ?? "—"}</div>
                            <div><b>Customer:</b> {selectedRow?.customer_name ?? "—"}</div>
                            <div><b>Settlement Value:</b> {money(selectedRow?.settelment_value)}</div>
                        </div>

                        <div className="flex justify-end gap-2 border-t p-4">
                            <button
                                onClick={closeUpdateModal}
                                disabled={updateLoading}
                                className="rounded-xl border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateSettlement}
                                disabled={updateLoading}
                                className="rounded-xl bg-[#01C37D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#00a96b] disabled:opacity-50"
                            >
                                {updateLoading ? "Updating..." : "Confirm Update"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Update confimation modal */}
            {paymentModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-start justify-between border-b p-4">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 rounded-xl bg-[#EEEAFE] p-2 text-[#705CFE]">
                                    <IndianRupee size={18} />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold">Payment Update</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Update payment values for this order.
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={closePaymentModal}
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                                disabled={paymentLoading}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">User ID</div>
                                <input
                                    type="text"
                                    value={paymentForm.userid}
                                    disabled
                                    className="h-10 w-full rounded-xl border bg-gray-100 px-3 text-sm outline-none"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Order ID</div>
                                <input
                                    type="text"
                                    value={paymentForm.orderid}
                                    disabled
                                    className="h-10 w-full rounded-xl border bg-gray-100 px-3 text-sm outline-none"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Subtotal</div>
                                <input
                                    type="text"
                                    value={paymentForm.subtotal}
                                    onChange={(e) => handlePaymentInput("subtotal", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter subtotal"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Order Amount</div>
                                <input
                                    type="text"
                                    value={paymentForm.orderAmount}
                                    onChange={(e) => handlePaymentInput("orderAmount", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter order amount"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Pay Total</div>
                                <input
                                    type="text"
                                    value={paymentForm.payTotal}
                                    onChange={(e) => handlePaymentInput("payTotal", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter pay total"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Tax SGST</div>
                                <input
                                    type="text"
                                    value={paymentForm.taxsgst}
                                    onChange={(e) => handlePaymentInput("taxsgst", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter tax sgst"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Tax CGST</div>
                                <input
                                    type="text"
                                    value={paymentForm.taxcgst}
                                    onChange={(e) => handlePaymentInput("taxcgst", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter tax cgst"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Tax On Commission</div>
                                <input
                                    type="text"
                                    value={paymentForm.taxOnCommision}
                                    onChange={(e) => handlePaymentInput("taxOnCommision", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter tax on commission"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Chaafo Commission</div>
                                <input
                                    type="text"
                                    value={paymentForm.chaafoCommision}
                                    onChange={(e) => handlePaymentInput("chaafoCommision", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter chaafo commission"
                                />
                            </label>

                            <label className="text-sm">
                                <div className="mb-1 text-xs font-medium text-gray-500">Settlement Amount</div>
                                <input
                                    type="text"
                                    value={paymentForm.settlementAmount}
                                    onChange={(e) => handlePaymentInput("settlementAmount", e.target.value)}
                                    className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                                    placeholder="Enter settlement amount"
                                />
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 border-t p-4">
                            <button
                                onClick={closePaymentModal}
                                disabled={paymentLoading}
                                className="rounded-xl border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePaymentUpdate}
                                disabled={paymentLoading}
                                className="rounded-xl bg-[#705CFE] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5B47F5] disabled:opacity-50"
                            >
                                {paymentLoading ? "Updating..." : "Submit Payment Update"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}